/**
 * React context that hands a singleton `ApiClient` to descendant hooks.
 *
 * Wrap the app tree once near the top:
 *
 * ```tsx
 * <ApiClientProvider options={{ baseUrl: import.meta.env.VITE_API_URL }}>
 *   <App />
 * </ApiClientProvider>
 * ```
 *
 * Hooks then call `useApiClient()` to read the singleton — the context
 * memoizes on `baseUrl` + `fetch` so the same client identity is reused
 * across renders (avoids re-running react-query effects).
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';

import { createApiClient, type ApiClient, type ApiClientOptions } from './hc';

const ApiClientContext = createContext<ApiClient | null>(null);

export interface ApiClientProviderProps {
  options: ApiClientOptions;
  /** Override for tests — pass a pre-built client to skip the factory. */
  client?: ApiClient;
  children: ReactNode;
}

export function ApiClientProvider({
  options,
  client: clientOverride,
  children,
}: ApiClientProviderProps) {
  const client = useMemo<ApiClient>(
    () => clientOverride ?? createApiClient(options),
    // We deliberately depend on the few ApiClientOptions fields that affect
    // identity. `onError` and `mintCorrelationId` can change per render
    // without forcing a new client (they're called per-request).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientOverride, options.baseUrl, options.fetch, options.credentials],
  );
  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

export function useApiClient(): ApiClient {
  const c = useContext(ApiClientContext);
  if (!c) {
    throw new Error('@barel/api-client: useApiClient must be used inside <ApiClientProvider>.');
  }
  return c;
}
